import { useState } from "react";
import {
  ArrowRightOutlined,
  PlayCircleFilled,
  RocketOutlined,
} from "@ant-design/icons";
import { Button, Modal, Tag } from "antd";
import { useSelector } from "react-redux";
import "../style/games.scss";

const GAME_ROOT = "https://100-days100-games.vercel.app/minigames";

const games = [
  {
    title: "Duck Shooter",
    slug: "duck-shooter",
    image: "duck-shooter-clean.png",
    genre: "Arcade",
  },
  {
    title: "The Bandit Hunter",
    slug: "the-bandit-hunter",
    image: "the-bandit-hunter.jpg",
    genre: "Action",
    accent: "bandit",
  },
  {
    title: "Zombie Redemption",
    slug: "zombie-redemption",
    genre: "Action",
    image: "zombie-redemption.png",
  },
  {
    title: "Charlie's Kitty Adventure",
    slug: "charlies-kitty-adventure",
    image: "charlies-kitty-adventure-clean.png",
    genre: "Adventure",
  },
  {
    title: "Battleship War",
    slug: "battleship-war",
    image: "battleship-war-clean.png",
    genre: "Strategy",
  },
  {
    title: "Snake Attack",
    slug: "snake-attack",
    image: "snake-attack-clean.png",
    genre: "Arcade",
  },
  {
    title: "8 Ball Pool",
    slug: "8-ball-pool",
    image: "8-ball-pool-clean.png",
    genre: "Sports",
  },
  {
    title: "Ninja Veggie Slice",
    slug: "ninja-veggie-slice",
    image: "ninja-veggie-slice-clean.png",
    genre: "Arcade",
  },
  {
    title: "One Stroke",
    slug: "one-stroke",
    image: "one-stroke-clean.png",
    genre: "Puzzle",
  },
  {
    title: "Mahjong",
    slug: "mahjong",
    image: "mahjong-clean.png",
    genre: "Puzzle",
  },
  {
    title: "Hidden Kitty",
    slug: "hidden-kitty",
    image: "hidden-kitty-clean.png",
    genre: "Puzzle",
  },
  {
    title: "Candy Pop",
    slug: "candy-pop",
    image: "candy-pop-clean.png",
    genre: "Casual",
  },
  {
    title: "Block Pictures",
    slug: "block-pictures",
    image: "block-pictures-clean.png",
    genre: "Puzzle",
  },
].map((game) => ({
  ...game,
  url: `${GAME_ROOT}/${game.slug}/index.html`,
  imageUrl: game.image ? `/assets/games/${game.image}` : null,
}));

const Games = ({ title }) => {
  document.title = title;
  const themeMode = useSelector((state) => state.themeMode);
  const [activeGame, setActiveGame] = useState(null);

  return (
    <div
      className={`games-page ${themeMode.themeMode ? "games-page-dark" : ""}`}
    >
      <section className="games-hero">
        <div className="games-hero-copy">
          <Tag bordered={false}>
            <RocketOutlined /> Break room
          </Tag>
          <h1>Pick a game. Take a breather.</h1>
          <p>
            Quick browser games you can play without leaving your Resume Builder
            workspace.
          </p>
        </div>
        <div className="games-count">
          <strong>{games.length}</strong>
          <span>games ready</span>
        </div>
      </section>

      <section className="games-library" aria-label="Game library">
        <div className="games-library-heading">
          <div>
            <h2>Game library</h2>
            <p>Choose a title to launch it inside the app.</p>
          </div>
        </div>
        <div className="games-grid">
          {games.map((game, index) => (
            <article
              className={`game-card ${game.accent ? `game-card-${game.accent}` : ""}`}
              key={game.slug}
              onClick={() => setActiveGame(game)}
            >
              <div className="game-art">
                {game.imageUrl ? (
                  <img
                    src={game.imageUrl}
                    alt={`${game.title} game banner`}
                    loading="lazy"
                  />
                ) : (
                  <div className="game-art-fallback" aria-hidden="true">
                    <span>{index === 1 ? "BH" : "ZR"}</span>
                  </div>
                )}
                <div className="game-play-overlay">
                  <PlayCircleFilled />
                </div>
                <Tag bordered={false}>{game.genre}</Tag>
              </div>
              <div className="game-card-body">
                <div>
                  <h3>{game.title}</h3>
                  <span>Play instantly</span>
                </div>
                <Button
                  type="text"
                  aria-label={`Play ${game.title}`}
                  icon={<ArrowRightOutlined />}
                />
              </div>
            </article>
          ))}
        </div>
      </section>

      <Modal
        className="game-player-modal"
        open={Boolean(activeGame)}
        onCancel={() => setActiveGame(null)}
        width="min(96vw, 1440px)"
        centered
        destroyOnHidden
        title={
          activeGame && (
            <div className="game-player-title">
              <PlayCircleFilled />
              <span>{activeGame.title}</span>
            </div>
          )
        }
        footer={
          activeGame && [
            <Button
              key="close"
              type="primary"
              onClick={() => setActiveGame(null)}
            >
              Close game
            </Button>,
          ]
        }
      >
        {activeGame && (
          <div className="game-frame-shell">
            <iframe
              title={activeGame.title}
              src={activeGame.url}
              allow="autoplay; fullscreen; gamepad; clipboard-read; clipboard-write"
              allowFullScreen
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-pointer-lock allow-modals"
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Games;
